import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, KeyRound, MailCheck } from "lucide-react";

const inputCls = "w-full px-4 py-3.5 rounded-xl text-sm";

function Frame({ children }) {
  return (
    <div className="min-h-screen bg-navy flex items-center justify-center px-6">
      <div className="w-full max-w-sm text-center">
        <img src="/logo.jpg" alt="Carlos Jr Fretes e Mudanças" className="h-24 w-auto object-contain mx-auto mb-5 rounded-xl bg-white p-1.5" />
        {children}
        <Link to="/admin" className="block mt-5 text-xs text-slate-400 underline" data-testid="recovery-back-login">
          Voltar ao login
        </Link>
      </div>
    </div>
  );
}

export default function AdminRecovery() {
  const [login, setLogin] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async () => {
    setLoading(true);
    try {
      await api.post("/auth/admin/esqueci-senha", { login });
      setSent(true);
    } catch {
      setSent(true); // resposta genérica proposital (não revela se o acesso existe)
    } finally {
      setLoading(false);
    }
  };

  return (
    <Frame>
      <div data-testid="admin-recovery-page">
        {sent ? (
          <>
            <MailCheck className="w-10 h-10 text-[#F5B301] mx-auto mb-3" />
            <h1 className="font-heading text-xl font-bold text-white">Verifique seu e-mail</h1>
            <p className="text-slate-300 text-sm mt-3 leading-relaxed" data-testid="admin-recovery-sent">
              Se o acesso existir, enviamos um link de redefinição para o e-mail cadastrado. O link expira em 1 hora.
            </p>
          </>
        ) : (
          <>
            <KeyRound className="w-10 h-10 text-[#F5B301] mx-auto mb-3" />
            <h1 className="font-heading text-xl font-bold text-white">Recuperar senha</h1>
            <p className="text-slate-300 text-sm mt-2 mb-5">Informe seu login ou e-mail administrativo.</p>
            <input
              data-testid="admin-recovery-input"
              className={`${inputCls} mb-4`}
              placeholder="Login ou e-mail"
              value={login}
              onChange={(e) => setLogin(e.target.value)}
            />
            <button
              data-testid="admin-recovery-btn"
              onClick={submit}
              disabled={loading || !login}
              className="w-full py-4 rounded-2xl bg-[#F5B301] text-navy font-heading font-extrabold text-sm active:scale-95 transition-transform disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "ENVIAR LINK DE REDEFINIÇÃO"}
            </button>
          </>
        )}
      </div>
    </Frame>
  );
}

export function AdminResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [s1, setS1] = useState("");
  const [s2, setS2] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (s1.length < 6) return toast.error("A senha deve ter pelo menos 6 caracteres");
    if (s1 !== s2) return toast.error("As senhas não coincidem");
    setBusy(true);
    try {
      await api.post("/auth/admin/redefinir-senha", { token, nova_senha: s1 });
      toast.success("Senha redefinida! Entre com a nova senha.");
      navigate("/admin", { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Link inválido ou expirado");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Frame>
      <div data-testid="admin-reset-page">
        <KeyRound className="w-10 h-10 text-[#F5B301] mx-auto mb-3" />
        <h1 className="font-heading text-xl font-bold text-white">Nova senha</h1>
        {!token && <p className="text-red-300 text-sm mt-3" data-testid="admin-reset-no-token">Link inválido: token ausente.</p>}
        <div className="relative mt-4 mb-3">
          <input
            data-testid="admin-reset-senha-input"
            type={show ? "text" : "password"}
            className={`${inputCls} pr-12`}
            placeholder="Nova senha (mín. 6 caracteres)"
            value={s1}
            onChange={(e) => setS1(e.target.value)}
          />
          <button type="button" data-testid="admin-reset-senha-toggle" onClick={() => setShow((s) => !s)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-navy">
            {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
        <input
          data-testid="admin-reset-senha2-input"
          type={show ? "text" : "password"}
          className={`${inputCls} mb-4`}
          placeholder="Repita a nova senha"
          value={s2}
          onChange={(e) => setS2(e.target.value)}
        />
        <button
          data-testid="admin-reset-btn"
          onClick={submit}
          disabled={busy || !token || !s1 || !s2}
          className="w-full py-4 rounded-2xl bg-[#F5B301] text-navy font-heading font-extrabold text-sm active:scale-95 transition-transform disabled:opacity-60"
        >
          {busy ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "REDEFINIR SENHA"}
        </button>
      </div>
    </Frame>
  );
}
